import { useRouter } from 'next/router'
import React, { useMemo } from 'react'
import { DateTime } from 'luxon'

import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import { useAuthentication } from '@hooks/useAuthentication'
import { useReadFreePromotion } from '@hooks/chapter/useReadFreePromotion'
import { ChapterStatusEnum } from '@interfaces/ChapterStatusEnum'
import { useDrawer } from '@hooks/contexts/DrawerContext/DrawerContext'
import { useStoryReader } from '@hooks/book/useStoryReader'
import { ChapterManageListProps, WithChapterManageListProps } from './interface'
import { PromotionReadFreeEnum } from '@interfaces/PromotionTypeEnum'
import { gtagBeginCheckout } from '@lib/gtag'
import { fbqBeginCheckout } from '@lib/facebookPixel'
import { ttqBeginCheckout } from '@lib/tiktok'

export function withChapterManageList(
  Component: React.FC<ChapterManageListProps>
) {
  function WithChapterManageList({
    data,
    bookId,
    bookName,
    bookCategory,
    order,
    isDrawer,
    isManga,
    isOwner,
    isSelect,
    selectedChapters,
    handleSelectChapter,
    canAction,
    bookType,
    bookReadPromotionDetail,
    bookChapterCampaign,
  }: WithChapterManageListProps) {
    const { isBuyAllChapterFetching } = useStoryReader()
    const router = useRouter()
    const loginModal = useModal({ modal: 'login' })
    const buyChapterModal = useModal({ modal: 'buyChapter' }, true)
    const notEnoughCoinModal = useModal({
      modal: 'notEnoughCoin',
      modalProps: {
        describeText: 'เนื้อเรื่องที่คุณเลือกอ่านติดเหรียญอยู่',
        purposeText: 'เข้าถึงเนื้อหานี้',
      },
    })
    const notEnoughReadFreeQuotaModal = useModal({
      modal: 'notEnoughReadFreeQuota',
      modalProps: {
        quota: 0,
        notEnoughCoin: false,
      },
    })
    const changeEmailModal = useModal({ modal: 'changeEmail' }, true)
    const storyDrawer = useDrawer({ drawer: 'story' })
    const { isAuthenticated, user, userCoin } = useAuthentication()
    const isRestricted =
      data.status === ChapterStatusEnum.RESTRICTED && !data.isPaid
    const { isReadFreePromotion, getTodayReadCount } = useReadFreePromotion(
      bookReadPromotionDetail
    )
    const buyable = useMemo(
      () =>
        !isRestricted &&
        canAction &&
        data.price > 0 &&
        !data.isPaid &&
        !isOwner &&
        !user.isAdmin &&
        isAuthenticated &&
        !user.emailEditable,
      [isRestricted, canAction, data, user, isOwner, isAuthenticated]
    )

    async function goToReadingPage() {
      router.push(`/story/${bookId}/chapter/${data.id}`)
    }

    function buyChapter(summaryCurrentCoin: number) {
      const now = DateTime.now().toUTC()
      const hasActiveCampaign =
        !!data.chapterCampaign &&
        DateTime.fromJSDate(new Date(data.chapterCampaign.startedAt)) <= now &&
        DateTime.fromJSDate(new Date(data.chapterCampaign.endedAt)) >= now
      const sellingPrice = hasActiveCampaign
        ? data.chapterCampaign.sellingPrice
        : data.price
      const buyChapterModalPayload = {
        chapterId: data.id,
        bookId,
        price: sellingPrice,
        bookName,
        chapterTitle: data.chapterTitle,
        chapterName: data.chapterName,
        bookType,
        bookCategory,
        onSuccess: () => {},
      }
      const analyticPayload = {
        value: sellingPrice,
        items: [
          {
            item_id: data.id,
            item_name: data.chapterName,
            item_category: bookCategory.id,
            item_variant: bookType,
            price: sellingPrice,
          },
        ],
      }

      if (
        bookReadPromotionDetail?.currentReadPromotion?.readPromotionType ===
          PromotionReadFreeEnum.READ_FREE_PER_DAY &&
        getTodayReadCount(now) >=
          (bookReadPromotionDetail?.currentReadPromotion?.readFreePerDay ??
            0) &&
        now.valueOf() <
          DateTime.fromISO(
            bookReadPromotionDetail?.currentReadPromotion?.endDate
          )
            .toUTC()
            .valueOf()
      ) {
        notEnoughReadFreeQuotaModal.show({
          quota:
            bookReadPromotionDetail?.currentReadPromotion?.readFreePerDay ?? 0,
          notEnoughCoin: summaryCurrentCoin < data.price,
          onClickBuy: () => {
            buyChapterModal.show(buyChapterModalPayload)

            gtagBeginCheckout(analyticPayload)
            fbqBeginCheckout(analyticPayload)
            ttqBeginCheckout(analyticPayload)
          },
          onClickRedeem: () => notEnoughCoinModal.show(),
        })
        return
      }

      if (!data.isPaid && summaryCurrentCoin >= sellingPrice) {
        buyChapterModal.show(buyChapterModalPayload)

        gtagBeginCheckout(analyticPayload)
        fbqBeginCheckout(analyticPayload)
        ttqBeginCheckout(analyticPayload)
        return
      }

      notEnoughCoinModal.show()
    }

    async function handleClickChapter() {
      if (isRestricted || !canAction || isBuyAllChapterFetching) return

      let summaryCurrentCoin = 0
      if (userCoin.paidCoin) {
        summaryCurrentCoin += userCoin.paidCoin.value
      }

      if (userCoin.freeCoin) {
        summaryCurrentCoin += Math.floor(userCoin.freeCoin.value)
      }

      if (userCoin.thirdCoin) {
        summaryCurrentCoin += Math.floor(userCoin.thirdCoin.value)
      }

      if (
        data.price === 0 ||
        data.isPaid ||
        isOwner ||
        user.isAdmin ||
        (isReadFreePromotion(data.id) && isAuthenticated)
      ) {
        await goToReadingPage()
        if (isDrawer) {
          storyDrawer.hide()
        }
      } else if (!isAuthenticated) {
        loginModal.show()
      } else if (user.emailEditable) {
        changeEmailModal.show({
          onSuccess: () => {
            buyChapter(summaryCurrentCoin)
          },
        })
      } else {
        buyChapter(summaryCurrentCoin)
      }
    }

    const componentProps = {
      data,
      order,
      isRestricted,
      bookCategory,
      isManga,
      isDrawer,
      isSelect,
      selectedChapters,
      handleSelectChapter,
      buyable,
      isBuyAllChapterFetching,
      bookReadPromotionDetail,
      bookChapterCampaign,
      isReadFreePromotion,
      handleClickChapter,
    }

    return <Component {...componentProps} />
  }

  return WithChapterManageList
}
